import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Save, RefreshCw, Image, Trash2, ExternalLink, Upload, Power } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CONTENT_KEYS = [
  { key: 'home_welcome', label: 'Ana Sayfa Karşılama', description: 'Ana sayfada gösterilen karşılama mesajı' },
  { key: 'about', label: 'Hakkımızda', description: 'Hakkımızda sayfası içeriği' },
  { key: 'terms', label: 'Kullanım Koşulları', description: 'Kullanım koşulları sayfası içeriği' },
  { key: 'privacy', label: 'Gizlilik Politikası', description: 'Gizlilik politikası sayfası içeriği' },
  { key: 'kvkk', label: 'KVKK', description: 'KVKK aydınlatma metni içeriği' },
];

export default function AdminContent() {
  const [contents, setContents] = useState({});
  const [originalContents, setOriginalContents] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [activeTab, setActiveTab] = useState('home_welcome');
  
  // Landing image state
  const [landingImage, setLandingImage] = useState('');
  const [savingLandingImage, setSavingLandingImage] = useState(false);

  // Desktop landing on/off: when off, desktop visitors see only the logo and this notice.
  const [landingEnabled, setLandingEnabled] = useState(true);
  const [landingNotice, setLandingNotice] = useState('');
  const [savedLandingNotice, setSavedLandingNotice] = useState('');
  const [savingLandingToggle, setSavingLandingToggle] = useState(false);

  // QR link state
  const [qrLink, setQrLink] = useState('');
  const [qrLinkInput, setQrLinkInput] = useState('');
  const [savingQrLink, setSavingQrLink] = useState(false);

  // Headline state
  const [headlineText, setHeadlineText] = useState('');
  const [headlineTextInput, setHeadlineTextInput] = useState('');
  const [headlineIcon, setHeadlineIcon] = useState('star');
  const [headlineIconInput, setHeadlineIconInput] = useState('star');
  const [savingHeadline, setSavingHeadline] = useState(false);

  // Logo states
  const [loginLogo, setLoginLogo] = useState('');
  const [loginLogoInput, setLoginLogoInput] = useState('');
  const [homeLogo, setHomeLogo] = useState('');
  const [homeLogoInput, setHomeLogoInput] = useState('');
  const [savingLogos, setSavingLogos] = useState(false);

  useEffect(() => {
    fetchContents();
    fetchLandingImage();
    fetchLandingToggle();
    fetchQrLink();
    fetchHeadline();
    fetchLogos();
  }, []);

  const fetchLandingImage = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/landing_image`);
      if (response.ok) {
        const data = await response.json();
        if (data.value) setLandingImage(data.value);
      }
    } catch (error) {
      console.error('Error fetching landing image:', error);
    }
  };

  // The landing image lives in the public "site-images" bucket; the setting
  // stores its public URL. Replacing or clearing removes the previous file.
  const LANDING_BUCKET = 'site-images';
  const LANDING_MAX_BYTES = 5 * 1024 * 1024;
  const LANDING_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  const landingStoragePath = (url) => {
    const marker = `/storage/v1/object/public/${LANDING_BUCKET}/`;
    const i = (url || '').indexOf(marker);
    return i === -1 ? null : decodeURIComponent(url.slice(i + marker.length).split('?')[0]);
  };

  const removeLandingFile = async (url) => {
    const path = landingStoragePath(url);
    if (path) await supabase.storage.from(LANDING_BUCKET).remove([path]).catch(() => {});
  };

  const fetchLandingToggle = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings`);
      if (!response.ok) return;
      const data = await response.json();
      setLandingEnabled(data.landing_enabled !== false);
      const notice = typeof data.landing_notice === 'string' ? data.landing_notice : '';
      setLandingNotice(notice);
      setSavedLandingNotice(notice);
    } catch (error) {
      console.error('Error fetching landing toggle:', error);
    }
  };

  const saveSetting = async (key, value) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`${BACKEND_URL}/api/admin/settings/${key}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({ value }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Ayar kaydedilemedi');
    }
  };

  const handleLandingToggle = async (enabled) => {
    setSavingLandingToggle(true);
    try {
      await saveSetting('landing_enabled', enabled);
      setLandingEnabled(enabled);
      toast.success(enabled ? 'Landing sayfası açıldı' : 'Landing sayfası kapatıldı; masaüstünde duyuru gösteriliyor');
    } catch (error) {
      toast.error(error.message || 'Ayar kaydedilemedi');
    } finally {
      setSavingLandingToggle(false);
    }
  };

  const handleSaveLandingNotice = async () => {
    setSavingLandingToggle(true);
    try {
      const notice = landingNotice.trim();
      await saveSetting('landing_notice', notice);
      setLandingNotice(notice);
      setSavedLandingNotice(notice);
      toast.success('Duyuru metni kaydedildi');
    } catch (error) {
      toast.error(error.message || 'Duyuru kaydedilemedi');
    } finally {
      setSavingLandingToggle(false);
    }
  };

  const saveLandingImageSetting = async (value) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`${BACKEND_URL}/api/admin/settings/landing_image`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({ value }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Ayar kaydedilemedi');
    }
  };

  const handleLandingImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!LANDING_TYPES.includes(file.type)) { toast.error('Sadece JPG, PNG veya WEBP yükleyebilirsiniz'); return; }
    if (file.size > LANDING_MAX_BYTES) { toast.error("Dosya 5MB'dan küçük olmalı"); return; }

    setSavingLandingImage(true);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `landing/landing_${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from(LANDING_BUCKET)
        .upload(path, file, { contentType: file.type, upsert: false, cacheControl: '31536000' });
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from(LANDING_BUCKET).getPublicUrl(path);
      await saveLandingImageSetting(publicUrl);
      const previous = landingImage;
      setLandingImage(publicUrl);
      await removeLandingFile(previous);
      toast.success('Landing görseli yüklendi');
    } catch (error) {
      console.error('Error uploading landing image:', error);
      toast.error('Görsel yüklenemedi: ' + (error.message || ''));
    } finally {
      setSavingLandingImage(false);
    }
  };

  const handleClearLandingImage = async () => {
    setSavingLandingImage(true);
    try {
      await saveLandingImageSetting('');
      await removeLandingFile(landingImage);
      setLandingImage('');
      toast.success('Landing görseli kaldırıldı');
    } catch (error) {
      console.error('Error clearing landing image:', error);
      toast.error('Görsel kaldırılırken hata oluştu');
    } finally {
      setSavingLandingImage(false);
    }
  };

  const fetchQrLink = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/qr_link`);
      if (response.ok) {
        const data = await response.json();
        if (data.value) {
          setQrLink(data.value);
          setQrLinkInput(data.value);
        }
      }
    } catch (error) {
      console.error('Error fetching QR link:', error);
    }
  };

  const handleSaveQrLink = async () => {
    setSavingQrLink(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${BACKEND_URL}/api/admin/settings/qr_link`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ value: qrLinkInput }),
      });
      if (!response.ok) throw new Error('Failed to save');
      setQrLink(qrLinkInput);
      toast.success('QR bağlantısı kaydedildi');
    } catch (error) {
      toast.error('QR bağlantısı kaydedilirken hata oluştu');
    } finally {
      setSavingQrLink(false);
    }
  };

  const fetchHeadline = async () => {
    try {
      const { data: textData } = await supabase.from('app_content').select('content').eq('key', 'headline_text').maybeSingle();
      if (textData?.content) { setHeadlineText(textData.content); setHeadlineTextInput(textData.content); }
    } catch (e) {}
    try {
      const { data: iconData } = await supabase.from('app_content').select('content').eq('key', 'headline_icon').maybeSingle();
      if (iconData?.content) { setHeadlineIcon(iconData.content); setHeadlineIconInput(iconData.content); }
    } catch (e) {}
  };

  const handleSaveHeadline = async () => {
    setSavingHeadline(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      // Save text via backend API
      await fetch(`${BACKEND_URL}/api/admin/content/headline_text`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ content: headlineTextInput })
      });
      // Save icon via backend API
      await fetch(`${BACKEND_URL}/api/admin/content/headline_icon`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ content: headlineIconInput })
      });
      
      setHeadlineText(headlineTextInput);
      setHeadlineIcon(headlineIconInput);
      toast.success('Manşet kaydedildi');
    } catch (error) {
      toast.error('Manşet kaydedilirken hata oluştu');
    } finally {
      setSavingHeadline(false);
    }
  };

  const fetchLogos = async () => {
    try {
      const { data: login } = await supabase.from('app_content').select('content').eq('key', 'login_logo').maybeSingle();
      if (login?.content) { setLoginLogo(login.content); setLoginLogoInput(login.content); }
      const { data: home } = await supabase.from('app_content').select('content').eq('key', 'home_logo').maybeSingle();
      if (home?.content) { setHomeLogo(home.content); setHomeLogoInput(home.content); }
    } catch (e) {}
  };

  const handleLogoUpload = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Sadece resim dosyaları'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Dosya 2MB\'dan küçük olmalı'); return; }
    
    setSavingLogos(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const fileName = `${key}_${Date.now()}.${file.name.split('.').pop()}`;
      
      // Upload to Supabase Storage
      const arrayBuffer = await file.arrayBuffer();
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('logos')
        .upload(fileName, arrayBuffer, { contentType: file.type, upsert: true });
      
      if (uploadError) throw uploadError;
      
      const publicUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/logos/${fileName}`;
      
      // Save URL to app_content
      await fetch(`${BACKEND_URL}/api/admin/content/${key}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ content: publicUrl })
      });
      
      if (key === 'login_logo') { setLoginLogo(publicUrl); setLoginLogoInput(publicUrl); }
      if (key === 'home_logo') { setHomeLogo(publicUrl); setHomeLogoInput(publicUrl); }
      toast.success('Logo yüklendi');
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error('Logo yüklenemedi: ' + (error.message || ''));
    } finally {
      setSavingLogos(false);
      e.target.value = '';
    }
  };

  const handleRemoveLogo = async (key) => {
    setSavingLogos(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      await fetch(`${BACKEND_URL}/api/admin/content/${key}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ content: '' })
      });
      
      if (key === 'login_logo') { setLoginLogo(''); setLoginLogoInput(''); }
      if (key === 'home_logo') { setHomeLogo(''); setHomeLogoInput(''); }
      toast.success('Logo kaldırıldı');
    } catch (error) {
      toast.error('Logo kaldırılamadı');
    } finally {
      setSavingLogos(false);
    }
  };

  const fetchContents = async () => {
    try {
      const { data, error } = await supabase
        .from('app_content')
        .select('key, content');

      if (error) throw error;

      const contentMap = {};
      (data || []).forEach(item => {
        contentMap[item.key] = item.content || '';
      });

      // Ensure all keys exist
      CONTENT_KEYS.forEach(({ key }) => {
        if (!contentMap[key]) {
          contentMap[key] = '';
        }
      });

      setContents(contentMap);
      setOriginalContents(contentMap);
    } catch (error) {
      console.error('Error fetching contents:', error);
      toast.error('İçerikler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (key, value) => {
    setContents(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));

    try {
      // Check if content exists
      const { data: existing } = await supabase
        .from('app_content')
        .select('key')
        .eq('key', key)
        .single();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('app_content')
          .update({ 
            content: contents[key],
            updated_at: new Date().toISOString()
          })
          .eq('key', key);

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('app_content')
          .insert({ 
            key,
            content: contents[key],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      setOriginalContents(prev => ({ ...prev, [key]: contents[key] }));
      toast.success('İçerik kaydedildi');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('İçerik kaydedilirken hata oluştu');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleReset = (key) => {
    setContents(prev => ({ ...prev, [key]: originalContents[key] }));
    toast.info('Değişiklikler geri alındı');
  };

  const hasChanges = (key) => contents[key] !== originalContents[key];

  return (
    <div data-testid="admin-content">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-brand text-2xl text-brand-ink">İçerik Yönetimi</h2>
      </div>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <Card>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <CardHeader className="pb-0">
              <TabsList className="w-full justify-start">
                {CONTENT_KEYS.map(({ key, label }) => (
                  <TabsTrigger 
                    key={key} 
                    value={key}
                    className="relative"
                    data-testid={`content-tab-${key}`}
                  >
                    {label}
                    {hasChanges(key) && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-brand-red rounded-full" />
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </CardHeader>

            <CardContent className="pt-6">
              {CONTENT_KEYS.map(({ key, label, description }) => (
                <TabsContent key={key} value={key} className="mt-0">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium text-lg">{label}</h3>
                      <p className="text-sm text-gray-500">{description}</p>
                    </div>

                    <Textarea
                      value={contents[key]}
                      onChange={(e) => handleContentChange(key, e.target.value)}
                      rows={15}
                      className="font-mono text-sm"
                      placeholder={`${label} içeriğini buraya yazın...`}
                      data-testid={`content-textarea-${key}`}
                    />

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        {contents[key].length} karakter
                        {hasChanges(key) && (
                          <span className="ml-2 text-brand-red-deep font-medium">• Kaydedilmemiş değişiklikler</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={() => handleReset(key)}
                          disabled={!hasChanges(key)}
                          data-testid={`content-reset-${key}`}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Geri Al
                        </Button>
                        <Button
                          onClick={() => handleSave(key)}
                          disabled={saving[key] || !hasChanges(key)}
                          data-testid={`content-save-${key}`}
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {saving[key] ? 'Kaydediliyor...' : 'Kaydet'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              ))}
            </CardContent>
          </Tabs>
        </Card>
      )}

      {/* Help Info */}
      <Card className="mt-6 bg-brand-yellow-soft border-brand-line">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-brand-ink mt-0.5" />
            <div>
              <h4 className="font-medium text-brand-ink">İçerik Düzenleme İpuçları</h4>
              <ul className="text-sm text-gray-700 mt-2 space-y-1">
                <li>• Her sekmedeki içerik ilgili sayfada görüntülenir</li>
                <li>• Değişiklikler kaydedilene kadar uygulanmaz</li>
                <li>• "Geri Al" butonu ile son kaydedilen içeriğe dönebilirsiniz</li>
                <li>• HTML etiketleri kullanabilirsiniz (dikkatli olun)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Desktop landing on/off */}
      <Card className={`mt-6 ${!landingEnabled ? 'border-2 border-brand-red' : ''}`} data-testid="landing-toggle-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <Power className="w-5 h-5" />
              Landing Sayfası
            </span>
            <span className="flex items-center gap-2 text-sm font-normal">
              {landingEnabled ? 'Açık' : 'Kapalı'}
              <Switch
                checked={landingEnabled}
                onCheckedChange={handleLandingToggle}
                disabled={savingLandingToggle}
                data-testid="landing-toggle"
              />
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-600">
            Masaüstünden www.appstars.tech'e girenlerin gördüğü QR kodlu açılış sayfası.
            Kapatınca masaüstünde yalnızca logo ve aşağıdaki duyuru görünür. Telefondaki uygulama etkilenmez.
          </p>
          {!landingEnabled && (
            <p className="text-sm text-brand-red-deep font-medium">
              Şu an kapalı: masaüstü ziyaretçileri duyuru sayfasını görüyor.
            </p>
          )}
          <div className="space-y-2">
            <label htmlFor="landing-notice" className="text-sm font-medium">Kapalıyken gösterilecek duyuru</label>
            <Textarea
              id="landing-notice"
              value={landingNotice}
              onChange={(e) => setLandingNotice(e.target.value)}
              maxLength={300}
              rows={2}
              placeholder="Çok yakında yeniden buradayız."
              data-testid="landing-notice-input"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Boş bırakılırsa "Çok yakında yeniden buradayız." yazar.</span>
              <Button
                onClick={handleSaveLandingNotice}
                disabled={savingLandingToggle || landingNotice.trim() === savedLandingNotice}
                data-testid="landing-notice-save"
              >
                <Save className="w-4 h-4 mr-2" />
                Kaydet
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Landing Page Image Upload */}
      <Card className="mt-6" data-testid="landing-image-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            Landing Page Görseli
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Masaüstünden siteye girenlere gösterilen açılış sayfasının sağ tarafındaki görsel.
            JPG, PNG veya WEBP, en fazla 5MB (önerilen: 1920x1080 gibi geniş, yatay bir görsel).
            Görsel yüklenmezse sağ tarafta Appstars logosu gösterilir.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              id="landing-image-file"
              onChange={handleLandingImageUpload}
              disabled={savingLandingImage}
            />
            <label
              htmlFor="landing-image-file"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border border-brand-ink bg-brand-neon text-brand-ink text-sm font-semibold cursor-pointer hover:bg-brand-neon/85 ${savingLandingImage ? 'opacity-50 pointer-events-none' : ''}`}
              data-testid="landing-image-upload"
            >
              <Upload className="w-4 h-4" />
              {savingLandingImage ? 'Yükleniyor...' : landingImage ? 'Görseli Değiştir' : 'Görsel Yükle'}
            </label>
            {landingImage && (
              <Button
                variant="destructive"
                onClick={handleClearLandingImage}
                disabled={savingLandingImage}
                data-testid="landing-image-clear"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Kaldır
              </Button>
            )}
          </div>

          {/* Preview */}
          {landingImage && (
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">Mevcut Görsel:</p>
              <div className="relative rounded-lg overflow-hidden border border-gray-200">
                <img 
                  src={landingImage} 
                  alt="Landing Preview" 
                  className="w-full h-48 object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200" viewBox="0 0 400 200"%3E%3Crect fill="%23f3f4f6" width="400" height="200"/%3E%3Ctext x="50%25" y="50%25" fill="%236b7280" text-anchor="middle" dy=".3em"%3EGörsel yüklenemedi%3C/text%3E%3C/svg%3E';
                  }}
                />
                <a 
                  href={landingImage} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="absolute top-2 right-2 bg-white/90 p-2 rounded-lg hover:bg-white transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* QR Code Link */}
      <Card className="mt-6" data-testid="qr-link-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="w-5 h-5" />
            QR Kod Baglantisi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Desktop landing sayfasindaki QR kodun yonlendirecegi URL. Bos birakilirsa mevcut site adresi kullanilir.
          </p>
          
          <div className="flex gap-2">
            <Input
              type="url"
              value={qrLinkInput}
              onChange={(e) => setQrLinkInput(e.target.value)}
              placeholder="https://example.com/download"
              className="flex-1"
              data-testid="qr-link-input"
            />
            <Button
              onClick={handleSaveQrLink}
              disabled={savingQrLink || qrLinkInput === qrLink}
              data-testid="qr-link-save"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingQrLink ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
          
          {qrLink && (
            <p className="text-sm text-green-600">Mevcut: {qrLink}</p>
          )}
        </CardContent>
      </Card>

      {/* Headline Management */}
      <Card className="mt-6" data-testid="headline-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Manset (Ana Sayfa)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Ana sayfada karsilama yazisinin altinda gosterilen manset metni ve ikonu.
          </p>
          
          <div>
            <label className="text-sm font-medium">Manset Metni</label>
            <Input
              value={headlineTextInput}
              onChange={(e) => setHeadlineTextInput(e.target.value)}
              placeholder="Daha fazla kampanyaya katilabilmek icin profilinizi yukseltin"
              className="mt-1"
              data-testid="headline-text-input"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium">Ikon</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                {id: 'star', label: 'Yildiz'}, {id: 'zap', label: 'Simek'}, {id: 'heart', label: 'Kalp'},
                {id: 'flame', label: 'Alev'}, {id: 'trophy', label: 'Kupa'}, {id: 'target', label: 'Hedef'},
                {id: 'sparkles', label: 'Isiltis'}, {id: 'gift', label: 'Hediye'}, {id: 'crown', label: 'Tac'},
                {id: 'rocket', label: 'Roket'}, {id: 'bell', label: 'Zil'}, {id: 'check', label: 'Onay'},
                {id: 'coins', label: 'Para'}, {id: 'map-pin', label: 'Konum'}
              ].map(icon => (
                <button
                  key={icon.id}
                  onClick={() => setHeadlineIconInput(icon.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                    headlineIconInput === icon.id 
                      ? 'bg-brand-neon text-brand-ink border-brand-ink font-semibold' 
                      : 'bg-white text-gray-600 border-gray-200 hover:border-brand-ink'
                  }`}
                  data-testid={`headline-icon-${icon.id}`}
                >
                  {icon.label}
                </button>
              ))}
            </div>
          </div>
          
          <Button
            onClick={handleSaveHeadline}
            disabled={savingHeadline || (headlineTextInput === headlineText && headlineIconInput === headlineIcon)}
            data-testid="headline-save"
          >
            <Save className="w-4 h-4 mr-2" />
            {savingHeadline ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </CardContent>
      </Card>

      {/* Logo Management */}
      <Card className="mt-6" data-testid="logo-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            Logo Görselleri
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Giriş ve ana sayfada varsayılan olarak Appstars logosu gösterilir. Farklı bir logo yüklerseniz o kullanılır; bej zeminde okunacak (koyu renkli, şeffaf arka planlı) bir görsel seçin. Varsayılana dönmek için "Kaldır" butonuna basın.
          </p>
          
          <div className="space-y-4">
            {/* Login Logo */}
            <div className="border rounded-lg p-4">
              <label className="text-sm font-medium">Giriş Sayfası Logo</label>
              {loginLogo ? (
                <div className="mt-2 flex items-center gap-3">
                  <img src={loginLogo} alt="Login logo" className="h-12 object-contain border rounded p-1" />
                  <Button variant="destructive" size="sm" onClick={() => handleRemoveLogo('login_logo')} data-testid="remove-login-logo">
                    <Trash2 className="w-3 h-3 mr-1" /> Kaldır
                  </Button>
                </div>
              ) : (
                <div className="mt-2">
                  <input type="file" accept="image/*" className="hidden" id="login-logo-file"
                    onChange={(e) => handleLogoUpload(e, 'login_logo')} />
                  <label htmlFor="login-logo-file"
                    className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-brand-ink hover:bg-gray-50 text-sm text-gray-600">
                    <Image className="w-4 h-4" /> Görsel Yükle
                  </label>
                </div>
              )}
            </div>

            {/* Home Logo */}
            <div className="border rounded-lg p-4">
              <label className="text-sm font-medium">Ana Sayfa Logo</label>
              {homeLogo ? (
                <div className="mt-2 flex items-center gap-3">
                  <img src={homeLogo} alt="Home logo" className="h-12 object-contain border rounded p-1" />
                  <Button variant="destructive" size="sm" onClick={() => handleRemoveLogo('home_logo')} data-testid="remove-home-logo">
                    <Trash2 className="w-3 h-3 mr-1" /> Kaldır
                  </Button>
                </div>
              ) : (
                <div className="mt-2">
                  <input type="file" accept="image/*" className="hidden" id="home-logo-file"
                    onChange={(e) => handleLogoUpload(e, 'home_logo')} />
                  <label htmlFor="home-logo-file"
                    className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-brand-ink hover:bg-gray-50 text-sm text-gray-600">
                    <Image className="w-4 h-4" /> Görsel Yükle
                  </label>
                </div>
              )}
            </div>
          </div>
          
          {savingLogos && <p className="text-sm text-gray-500">Yükleniyor...</p>}
        </CardContent>
      </Card>
    </div>
  );
}
